import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "./InfoGraphicDetail.css";
import { useSidebarData } from "../../hooks/useSidebarData";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { useInfographicActions, useInfographicDetail, useInfographicsList } from "../../hooks/useInfographics";
import { fetchInfographicDetail, storeInfographicTime } from "../../services/infographicsService";
import { infographicDetailFlow, infographicNeedsPurchase, infographicRouteFlow, promptInfographicSubscription } from './infographicsAccess';
import { InfographicsActivityModal, InfographicsNotesModal, InfographicsReportModal } from "./InfographicsModals";

const BACKDROP_FADE_MS = 360;
const EMPTY_INFOGRAPHIC = {};

function formatDuration(totalSeconds = 0) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

function sanitizeDescriptionHtml(value = "") {
  const rawValue = String(value || "");
  const htmlValue = /&lt;\/?[a-z][\s\S]*&gt;/i.test(rawValue)
    ? new DOMParser().parseFromString(rawValue, "text/html").documentElement
        .textContent || rawValue
    : rawValue;

  return DOMPurify.sanitize(htmlValue);
}

const InfographicsMediaBackdrop = ({ imageSrc }) => {
  const [layers, setLayers] = useState([
    {
      id: imageSrc,
      src: imageSrc,
      state: "active",
    },
  ]);

  useEffect(() => {
    setLayers((currentLayers) => {
      const currentLayer = currentLayers[currentLayers.length - 1];

      if (!currentLayer || currentLayer.src === imageSrc) {
        return currentLayers;
      }

      return [
        ...currentLayers.map((layer) => ({
          ...layer,
          state: "exit",
        })),
        {
          id: `${imageSrc}-${Date.now()}`,
          src: imageSrc,
          state: "enter",
        },
      ];
    });

    const timeoutId = window.setTimeout(() => {
      setLayers([
        {
          id: imageSrc,
          src: imageSrc,
          state: "active",
        },
      ]);
    }, BACKDROP_FADE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [imageSrc]);

  return (
    <div className="infographics-details-view__media-bg" aria-hidden="true">
      {layers.map((layer) => (
        <span
          key={layer.id}
          className={`infographics-details-view__media-bg-layer infographics-details-view__media-bg-layer--${layer.state}`}
          style={{
            backgroundImage: `url(${layer.src})`,
          }}
        />
      ))}

      <span className="infographics-details-view__media-bg-overlay" />
    </div>
  );
};

const InfographicDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data: sidebarData = null } = useSidebarData();
  const flow = useMemo(() => infographicRouteFlow(location.state), [location.state]);
  const initialId = Number(flow?.infographic_id || flow?.selectedInfographic?.id || 0);
  const [infographicId, setInfographicId] = useState(initialId);
  useEffect(() => setInfographicId(initialId), [initialId]);
  const [activeModal, setActiveModal] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState(1);
  const [navigating, setNavigating] = useState(false);
  const navigationBusy = useRef(false);
  const promptedId = useRef(0);
  const dialogOpen = useRef(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousFocus = document.activeElement;
    drawerRef.current?.querySelector('button')?.focus();
    const trapFocus = event => {
      if (event.key === 'Escape') { setDrawerOpen(false); return; }
      if (event.key !== 'Tab') return;
      const buttons = [...(drawerRef.current?.querySelectorAll('button:not(:disabled)') || [])];
      const first = buttons[0]; const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', trapFocus);
    return () => { document.removeEventListener('keydown', trapFocus); previousFocus?.focus?.(); };
  }, [drawerOpen]);

  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const elapsedSecondsRef = useRef(0);
  const lastStoredSecondsRef = useRef(-1);

  const stateId = resolveQuestionBankStateId({ locationState: flow, sidebarData });
  const subjectId = Number(flow?.selectedSubject?.id || flow?.subject_id || 0);
  const queryParams = useMemo(() => ({ states_id: stateId, subject_id: subjectId, category_id: 13, ...contentTopicRequest(flow), infographic_id: infographicId, selected_lang: flow?.selected_lang || flow?.language || "EN", ...(flow.ad_reward_id ? { ad_reward_id: flow.ad_reward_id } : {}) }), [flow, infographicId, stateId, subjectId]);
  const detailQuery = useInfographicDetail(queryParams, { enabled: Boolean(stateId && infographicId && subjectId) });
  const actions = useInfographicActions();
  const data = detailQuery.data?.infographic || EMPTY_INFOGRAPHIC;
  const blocked = infographicNeedsPurchase(data);
  const hasAccess = Boolean(data.infographic_id) && !blocked;
  const accessContext = useMemo(() => ({ ...flow, stateId, state_id: stateId, subjectId, subject_id: subjectId, category_id: 13 }), [flow, stateId, subjectId]);
  const drawerQuery = useInfographicsList({ ...queryParams, infographic_id: undefined, page: drawerPage }, { enabled: drawerOpen });
  const promptPurchase = useCallback(async (item) => {
    if (dialogOpen.current) return;
    dialogOpen.current = true;
    try { await promptInfographicSubscription(item, { ...accessContext,
      subscriptionBackTarget: { pathname: location.pathname, state: { ...flow, infographicId, infographic_id: infographicId } },
    }, navigate); }
    finally { dialogOpen.current = false; }
  }, [accessContext, navigate, location.pathname, flow, infographicId]);

  useEffect(() => {
    if (detailQuery.isFetching || !blocked || !data.infographic_id || promptedId.current === Number(data.infographic_id)) return;
    promptedId.current = Number(data.infographic_id);
    promptPurchase(data).catch(() => {});
  }, [blocked, data, promptPurchase, detailQuery.isFetching]);

  const selectInfographic = useCallback(async (item) => {
    const nextId = Number(item.id || item.infographic_id || 0);
    if (!nextId || nextId === infographicId || navigationBusy.current || dialogOpen.current) return;
    navigationBusy.current = true;
    setNavigating(true);
    try {
      // A reward token is verified/consumed by the backend; remaining counts alone
      // never grant access to protected content.
      if (infographicNeedsPurchase(item) && !flow.ad_reward_id) {
        setDrawerOpen(false);
        await promptPurchase(item);
        return;
      }
      const nextParams = { ...queryParams, infographic_id: nextId };
      const { payload } = await fetchInfographicDetail(nextParams);
      const candidate = payload.infographics_data;
      if (!candidate?.infographic_id) throw new Error('This infographic is unavailable.');
      if (infographicNeedsPurchase(candidate)) {
        setDrawerOpen(false);
        await promptPurchase(candidate);
        return;
      }
      client.setQueryData(['infographicDetail', nextParams], { ...payload, infographic: candidate });
      setInfographicId(nextId);
      setDrawerOpen(false);
      navigate(location.pathname, { replace: true, state: infographicDetailFlow(candidate, accessContext) });
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Unable to open infographic', text: error.message });
    } finally { navigationBusy.current = false; setNavigating(false); }
  }, [infographicId, flow.ad_reward_id, queryParams, promptPurchase, client, navigate, location.pathname, accessContext]);
  const activeItem = {
    id: Number(data.infographic_id || infographicId),
    serialNo: Number(data.infographic_index || 1),
    image: data.file_url || "",
    title:
      data.name ||
      (detailQuery.isLoading ? "Loading infographic..." : "Infographic"),
    description: data.description || data.summary || "",
    time: Number(data.timer_seconds || 0),
    nextId: Number(data.next_infographic_id || 0),
    previousId: Number(data.previous_infographic_id || 0),
    bookmarkStatus: Number(data.bookmark_status || 0) === 1,
    likeStatus: Number(data.like_status || 0) === 1,
    likeCount: Number(data.total_likes || 0),
    viewCount: Number(data.total_views || 0),
    noteCount: Number(data.total_infographics || 0),
    reportCount: data.total_reports || "0/0",
    viewStatus: Number(data.view_status || 0) === 1,
  };
  const detailActions = [
    {
      id: "like",
      icon: activeItem.likeStatus ? "bi-heart-fill" : "bi-heart",
      value: activeItem.likeCount,
    },
    {
      id: "read",
      icon: activeItem.viewStatus
        ? "bi-check2-circle"
        : "bi-exclamation-octagon-fill",
      value: activeItem.viewStatus ? "Read" : "Unread",
    },
    {
      id: "view",
      icon: "bi-eye",
      value: activeItem.viewCount,
      variant: "view",
    },
    {
      id: "save",
      icon: activeItem.bookmarkStatus ? "bi-bookmark-fill" : "bi-bookmark",
      value: "Save",
    },
    {
      id: "note",
      icon: "bi-pencil-square",
      value: activeItem.noteCount,
      variant: "notes",
    },
    {
      id: "report",
      icon: "bi-flag",
      value: activeItem.reportCount,
      variant: "reports",
    },
  ];
  const activeDotIndex = activeItem.previousId
    ? activeItem.nextId
      ? 1
      : 2
    : 0;

  useEffect(() => {
    setElapsedSeconds(Number(data.timer_seconds || 0));
    elapsedSecondsRef.current = Number(data.timer_seconds || 0);
    lastStoredSecondsRef.current = -1;
  }, [data.infographic_id, data.timer_seconds]);

  useEffect(() => {
    elapsedSecondsRef.current = elapsedSeconds;
  }, [elapsedSeconds]);

  const goToNext = useCallback(() => {
    if (activeItem.nextId) {
      selectInfographic({ id: activeItem.nextId, free_paid: data.next_infographic_free_paid });
    }
  }, [activeItem.nextId, data.next_infographic_free_paid, selectInfographic]);

  const goToPrevious = useCallback(() => {
    if (activeItem.previousId) {
      selectInfographic({ id: activeItem.previousId, free_paid: data.previous_infographic_free_paid });
    }
  }, [activeItem.previousId, data.previous_infographic_free_paid, selectInfographic]);

  const handlePointerDown = (event) => {
    // Only left mouse button
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    startYRef.current = event.clientY;
    currentYRef.current = event.clientY;

    setIsDragging(true);

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDragging) return;

    currentYRef.current = event.clientY;

    const difference = currentYRef.current - startYRef.current;

    /*
      Limit drag movement so article doesn't move too far.
    */
    const limitedDifference = Math.max(-220, Math.min(220, difference));

    setDragOffset(limitedDifference);
  };

  const handlePointerEnd = () => {
    if (!isDragging) return;

    const difference = currentYRef.current - startYRef.current;

    const swipeThreshold = 90;

    setIsDragging(false);
    setDragOffset(0);

    /*
      Drag UP
      difference is negative
      => next infographic
    */
    if (difference < -swipeThreshold) {
      goToNext();
      return;
    }

    /*
      Drag DOWN
      difference is positive
      => previous infographic
    */
    if (difference > swipeThreshold) {
      goToPrevious();
    }
  };

  /*
    Also support mouse wheel / trackpad.
    */
  const wheelLockRef = useRef(false);

  const handleWheel = (event) => {
    if (wheelLockRef.current || drawerOpen || activeModal || !hasAccess) return;

    if (Math.abs(event.deltaY) < 20) {
      return;
    }

    wheelLockRef.current = true;

    if (event.deltaY > 0) {
      goToNext();
    } else {
      goToPrevious();
    }

    window.setTimeout(() => {
      wheelLockRef.current = false;
    }, 500);
  };

  /*
    Keyboard support
  */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (drawerOpen || activeModal || !hasAccess || event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        goToNext();
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        goToPrevious();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goToNext, goToPrevious, drawerOpen, activeModal, hasAccess]);

  useEffect(() => {
    if (!hasAccess || !activeItem.id || !subjectId) return undefined;
    let intervalId = null;
    const startTimer = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(
        () => setElapsedSeconds((current) => current + 1),
        1000,
      );
    };
    const stopTimer = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };
    const persistTime = () => {
      const timerSeconds = Math.max(1, Math.floor(elapsedSecondsRef.current));
      if (lastStoredSecondsRef.current === timerSeconds) return;
      lastStoredSecondsRef.current = timerSeconds;
      storeInfographicTime({
        infographics_id: Number(activeItem.id),
        subject_id: Number(subjectId),
        timer_seconds: timerSeconds,
        is_view: 1,
        view_status: 1,
      }).catch(() => {
        lastStoredSecondsRef.current = -1;
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopTimer();
        persistTime();
      } else startTimer();
    };
    if (document.visibilityState === "visible") startTimer();
    window.addEventListener("pagehide", persistTime);
    window.addEventListener("popstate", persistTime);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => { stopTimer(); window.removeEventListener("pagehide", persistTime); window.removeEventListener("popstate", persistTime); document.removeEventListener("visibilitychange", handleVisibilityChange); persistTime(); };
  }, [activeItem.id, subjectId, hasAccess]);

  if (!hasAccess) return <HomeDashboardLayout showHeader={false}><div className="choose-state__status" role="status">
    <button type="button" onClick={() => navigate(-1)}>Go back</button>
    {detailQuery.isLoading ? <p>Loading infographic...</p> : detailQuery.error ? <><p role="alert">{detailQuery.error.message}</p><button type="button" onClick={() => detailQuery.refetch()}>Retry</button></> : blocked ? <>
      <p>Subscribe to unlock this infographic.</p><button type="button" onClick={() => promptPurchase(data)}>View plans</button>
    </> : <p>Open an infographic from the list to view its details.</p>}
  </div></HomeDashboardLayout>;

  return (
    <>
      <style>{`
@media (max-width: 767.98px) {
    .qbl-page {
        padding: 0;
    }
}
@media (max-width: 576px) {
    .qbl-page {
        padding: 0;
    }
}

@media (min-width: 481px) and (max-width: 1199px) {
    .wallet-students-section {
       padding: 50px 25px 15px;
    }
}

 `}</style>

      <HomeDashboardLayout
        showHeader={false}
        variant="infographic-detail-layout"
      >
        <div className="qbl-page qbankList-page">
          <div className="start-quiz qbl-main instruction-main">
          <nav className="infographic-detail-navigation" aria-label="Infographic navigation">
            <button type="button" className="infobtn" disabled={!activeItem.previousId || navigating} onClick={goToPrevious}>Previous</button>
            <button type="button" onClick={() => setDrawerOpen(true)}>All infographics</button>
            <button type="button" className="infobtn" disabled={!activeItem.nextId || navigating} onClick={goToNext}>Next</button>
          </nav>
            <div className="infographics-details-page" onWheel={handleWheel}>
              {/* =========================
                  MAIN ARTICLE
              ========================== */}
              <div className="infographics-details-feed">
                <section
                  className="infographics-details-page__inner"
                  key={activeItem.id}
                >
                  <article
                    className={`infographics-details-view ${
                      isDragging ? "infographics-details-view--dragging" : ""
                    }`}
                    aria-label={`Infographics detail ${activeItem.serialNo}`}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerEnd}
                    onPointerCancel={handlePointerEnd}
                    onPointerLeave={(event) => {
                      if (isDragging && event.pointerType === "mouse") {
                        handlePointerEnd();
                      }
                    }}
                    style={{
                      transform: `translate3d(0, ${dragOffset}px, 0)`,
                    }}
                  >
                    {/* =========================
                        MEDIA
                    ========================== */}
                    <div className="infographics-details-view__media-shell">
                      <button
                        type="button"
                        className="infographics-details-view__back-btn"
                        aria-label="Go back"
                        onPointerDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          window.history.back();
                        }}
                      >
                        <i className="bi bi-chevron-left" aria-hidden="true" />
                      </button>

                      <div className="infographics-details-view__media">
                        <InfographicsMediaBackdrop
                          imageSrc={activeItem.image}
                        />

                        {activeItem.image ? (
                          <img
                            src={activeItem.image}
                            alt={activeItem.title}
                            draggable="false"
                          />
                        ) : (
                          <div className="infographics-details-view__media-bg" />
                        )}

                        <div
                          className="infographics-details-view__overlay"
                          aria-hidden="true"
                        />
                      </div>

                      <div className="infographics-details-view__content">
                        <h3>{activeItem.title}</h3>

                        <div
                          className="infographics-details-view__description"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeDescriptionHtml(
                              activeItem.description,
                            ),
                          }}
                        />

                        <div className="infographics-details-view__time-chip">
                          <span
                            className="infographics-details-view__time-icon"
                            aria-hidden="true"
                          >
                            <i className="fa-regular fa-clock" />
                          </span>
                          <span>{formatDuration(elapsedSeconds)}</span>
                        </div>
                      </div>
                    </div>

                    {/* =========================
                        ACTION RAIL
                    ========================== */}
                    <aside
                      className="infographics-details-view__rail"
                      aria-label="Infographic actions"
                    >
                      <div className="infographics-details-view__rail-stack">
                        {detailActions.map((action) => (
                          <button
                            key={`${activeItem.id}-${action.id}`}
                            type="button"
                            className={`infographics-details-view__action ${
                              action.label ? "has-label" : "has-value"
                            }`}
                            aria-label={
                              action.label || action.value || action.id
                            }
                            onPointerDown={(event) => {
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (action.id === "like")
                                actions.like.mutate({
                                  infographics_id: activeItem.id,
                                });
                              else if (action.id === "save")
                                actions.bookmark.mutate({
                                  infographics_id: activeItem.id,
                                });
                              else if (action.id === "read")
                                actions.markRead.mutate({
                                  infographics_id: activeItem.id,
                                  subject_id: subjectId,
                                });
                              else setActiveModal(action.variant);
                            }}
                          >
                            <span
                              className="infographics-details-view__action-icon"
                              aria-hidden="true"
                            >
                              <i className={`bi ${action.icon}`} />
                            </span>

                            {action.value && (
                              <span className="infographics-details-view__action-value">
                                {action.value}
                              </span>
                            )}

                            {action.label && (
                              <span className="infographics-details-view__action-label">
                                {action.label}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </aside>
                  </article>
                </section>
              </div>

              {/* =========================
                  SWIPE HINT
              ========================== */}
              {activeItem.nextId && (
                <div className="infographics-details-swipe-hint">
                  {/* <i className="bi bi-chevron-up" /> */}
                  <span></span>
                </div>
              )}

              {/* =========================
                  PROGRESS DOTS
              ========================== */}
              <div
                className="infographics-details-dots"
                aria-label="Infographic position"
              >
                {[0, 1, 2].map((index) => (
                  <span
                    key={index}
                    className={index === activeDotIndex ? "active" : ""}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <InfographicsActivityModal
          item={activeItem}
          open={activeModal === "view"}
          onClose={() => setActiveModal(null)}
        />
        <InfographicsNotesModal
          item={activeItem}
          open={activeModal === "notes"}
          onClose={() => setActiveModal(null)}
        />
        <InfographicsReportModal
          item={activeItem}
          open={activeModal === "reports"}
          onClose={() => setActiveModal(null)}
        />
        <MyWalletSidebar withTopOffset={false} />
        {drawerOpen && <><button className="infographic-access-backdrop" aria-label="Close infographic drawer" onClick={() => setDrawerOpen(false)} />
        <aside ref={drawerRef} role="dialog" aria-modal="true" aria-label="Infographic drawer" className="infographic-access-drawer">
          <button type="button" onClick={() => setDrawerOpen(false)}>Close drawer</button>
          {drawerQuery.isLoading && <p>Loading infographics...</p>}
          {drawerQuery.error && <p role="alert">{drawerQuery.error.message}</p>}
          {drawerQuery.data?.items?.map(item => <button type="button" key={item.id} disabled={navigating} aria-current={Number(item.id) === infographicId ? 'true' : undefined} onClick={() => selectInfographic(item)}>
            {infographicNeedsPurchase(item) ? '🔒 ' : ''}{item.title}
          </button>)}
          <button type="button" disabled={drawerPage <= 1 || drawerQuery.isFetching} onClick={() => setDrawerPage(page => page - 1)}>Previous page</button>
          <button type="button" disabled={drawerPage >= Number(drawerQuery.data?.pagination?.totalPages || 1) || drawerQuery.isFetching} onClick={() => setDrawerPage(page => page + 1)}>Next page</button>
        </aside></>}
      </HomeDashboardLayout>
    </>
  );
};

export default InfographicDetail;
